import { validTablePing, type TablePing } from '@v2/multiplayer'
import type { CommandReceipt, GameListing, RoomCommand, RoomCreated, RoomEvent, RoomSummary, RoomView } from '@v2/multiplayer'
import { identityHeaders } from './identity'

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline'
export interface RoomConnectionState {
  readonly room: RoomView | null
  readonly status: ConnectionStatus
  readonly pending: boolean
  readonly error: string | null
  /** Set when the server says this browser is not (or no longer) part of the room. */
  readonly gone: boolean
}

export class RoomRequestError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) { super(message) }
}

export async function request<T>(path: string, payload?: object, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  let timedOut = false
  const timeout = setTimeout(() => { timedOut = true; controller.abort() }, 15_000)
  signal?.addEventListener('abort', abort, { once: true })
  if (signal?.aborted) controller.abort()
  try {
    const response = await fetch(path, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      headers: { ...identityHeaders(), ...(payload ? { 'content-type': 'application/json' } : {}) },
      body: payload ? JSON.stringify(payload) : undefined,
    })
    const result: unknown = await response.json().catch(() => null)
    if (!response.ok) {
      const problem = result && typeof result === 'object' ? result as { error?: string; code?: string } : {}
      throw new RoomRequestError(problem.error ?? 'The table could not be reached. Please try again.', problem.code ?? 'network-error', response.status)
    }
    if (!result) throw new RoomRequestError('The table returned an incomplete response.', 'invalid-response', response.status)
    return result as T
  } catch (cause) {
    if (timedOut) throw new RoomRequestError('The request timed out. Check your connection and try again.', 'request-timeout', 0)
    throw cause
  } finally {
    clearTimeout(timeout)
    signal?.removeEventListener('abort', abort)
  }
}

export const api = {
  games: () => request<{ games: GameListing[] }>('/api/games').then(result => result.games),
  myRooms: () => request<{ rooms: RoomSummary[] }>('/api/rooms/mine').then(result => result.rooms),
  createRoom: (name: string, gameId?: string) => request<RoomCreated>('/api/rooms', { name, gameId }),
  resolveCode: (code: string) => request<{ roomId: string; code: string }>(`/api/rooms/code/${encodeURIComponent(code)}`),
}

/** Projection ordering prevents a late HTTP response from undoing a newer socket update. */
export function newerRoom(current: RoomView | null, incoming: RoomView): RoomView {
  return current && incoming.version < current.version ? current : incoming
}

export type RoomAction = '/history' | '/start' | '/lobby' | '/bots' | '/bots/retry' | '/bots/pause' | '/bots/takeover' | '/bots/release' | '/leave'

/** HTTP commands are acknowledged; the socket streams only this member's private view. */
export class RoomConnection {
  private state: RoomConnectionState = { room: null, status: 'connecting', pending: false, error: null, gone: false }
  private pingListeners = new Set<(ping: TablePing) => void>()
  subscribePings = (listener: (ping: TablePing) => void) => { this.pingListeners.add(listener); return () => { this.pingListeners.delete(listener) } }
  sendPing = (position: TablePing["position"]) => {
    if (this.socket?.readyState === WebSocket.OPEN && this.state.room) this.socket.send(JSON.stringify({ type: "attention", ping: { position, generation: this.state.room.generation } }))
  }
  private listeners = new Set<() => void>()
  private socket: WebSocket | null = null
  private retry: ReturnType<typeof setTimeout> | null = null
  private running = false
  private attempt = 0
  private lifecycle = 0
  private loading: AbortController | null = null
  private lifetime = new AbortController()

  constructor(readonly roomId: string) {}
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener) } }
  getSnapshot = () => this.state
  private update(patch: Partial<RoomConnectionState>) {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach(listener => listener())
  }
  private accept(room: RoomView) { if (this.running) this.update({ room: newerRoom(this.state.room, room) }) }
  private path(action = '') { return `/api/rooms/${encodeURIComponent(this.roomId)}${action}` }

  start() {
    if (this.running) return
    if (this.lifetime.signal.aborted) this.lifetime = new AbortController()
    this.running = true
    this.connect()
    window.addEventListener('online', this.reconnect)
    window.addEventListener('offline', this.offline)
  }
  stop() {
    this.running = false
    this.lifecycle++
    this.lifetime.abort()
    this.update({ pending: false })
    this.loading?.abort()
    if (this.retry) clearTimeout(this.retry)
    this.retry = null
    this.socket?.close()
    this.socket = null
    window.removeEventListener('online', this.reconnect)
    window.removeEventListener('offline', this.offline)
  }
  private offline = () => this.update({ status: 'offline' })
  reconnect = () => {
    if (!this.running) return
    this.lifecycle++
    this.loading?.abort()
    if (this.retry) clearTimeout(this.retry)
    this.retry = null
    this.socket?.close()
    this.socket = null
    this.attempt = 0
    this.connect()
  }
  private connect() {
    if (!this.running) return
    const lifecycle = ++this.lifecycle
    this.update({ status: this.state.room ? 'reconnecting' : 'connecting' })
    this.loading?.abort()
    this.loading = new AbortController()
    void request<RoomView>(this.path(), undefined, this.loading.signal).then(room => {
      if (!this.running || lifecycle !== this.lifecycle) return
      this.accept(room)
      if (room.you) this.openSocket(lifecycle)
      else this.update({ status: 'connected', error: null })
    }).catch(cause => {
      if (!this.running || lifecycle !== this.lifecycle) return
      if (cause instanceof RoomRequestError && cause.status === 404) { this.update({ error: cause.message, gone: true, status: 'connected' }); return }
      this.update({ error: errorMessage(cause) })
      this.scheduleReconnect(lifecycle)
    })
  }
  private scheduleReconnect(lifecycle: number) {
    if (!this.running || lifecycle !== this.lifecycle) return
    this.update({ status: navigator.onLine ? 'reconnecting' : 'offline' })
    this.retry = setTimeout(() => this.connect(), Math.min(10_000, 500 * 2 ** this.attempt++))
  }
  private openSocket(lifecycle: number) {
    const url = new URL(this.path('/connect'), window.location.origin)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    const socket = new WebSocket(url)
    this.socket = socket
    socket.onopen = () => {
      if (!this.running || lifecycle !== this.lifecycle) return
      this.attempt = 0
      this.update({ status: 'connected', error: null })
    }
    socket.onmessage = event => {
      if (!this.running || lifecycle !== this.lifecycle) return
      try {
        const message = JSON.parse(String(event.data)) as RoomEvent
        if (message.type === 'attention' && validTablePing(message.ping) && message.ping.generation === this.state.room?.generation) this.pingListeners.forEach(listener => listener(message.ping))
        else if (message.type === 'room') this.accept(message.room)
        else if (message.type === 'error') this.update({ error: message.error })
      } catch { this.update({ error: 'The table sent an unreadable update. Reconnecting…' }); socket.close() }
    }
    socket.onclose = event => {
      if (!this.running || lifecycle !== this.lifecycle) return
      // 1008 means this browser is no longer a member; do not loop on reconnects.
      if (event.code === 1008) { this.update({ gone: true, status: 'connected' }); void this.refresh().catch(() => {}); return }
      this.scheduleReconnect(lifecycle)
    }
    socket.onerror = () => socket.close()
  }
  clearError = () => this.update({ error: null })
  private current(lifetime: AbortController) { return this.running && this.lifetime === lifetime && !lifetime.signal.aborted }
  async refresh() {
    const lifetime = this.lifetime
    const room = await request<RoomView>(this.path(), undefined, lifetime.signal)
    if (this.current(lifetime)) this.update({ room: newerRoom(this.state.room, room), gone: false })
    return room
  }
  private async mutate<T>(action: string, payload: object, refreshAfter = false): Promise<T> {
    const lifetime = this.lifetime
    if (!this.current(lifetime)) throw new Error('The room connection is closed.')
    if (this.state.pending) throw new Error('Please wait for the current request to finish.')
    this.update({ pending: true, error: null })
    try {
      const result = await request<T>(this.path(action), payload, lifetime.signal)
      if (!this.current(lifetime)) throw new Error('The room connection changed. Reconnect to check the latest table.')
      if (refreshAfter) await this.refresh()
      if (!this.current(lifetime)) throw new Error('The room connection changed. Reconnect to check the latest table.')
      return result
    }
    catch (cause) {
      if (this.current(lifetime)) {
        this.update({ error: errorMessage(cause) })
        await this.refresh().catch(() => {})
      }
      throw cause
    } finally { if (this.current(lifetime)) this.update({ pending: false }) }
  }
  async join(name: string) {
    const lifetime = this.lifetime
    const room = await this.mutate<RoomView>('/join', { name })
    if (!this.current(lifetime)) return
    this.update({ room: newerRoom(this.state.room, room), gone: false })
    this.reconnect()
  }
  async command(command: RoomCommand) {
    if (this.state.status !== 'connected') throw new Error('Reconnect before making a move.')
    // A GET closes the gap when a socket update is delayed or the connection just dropped.
    return this.mutate<CommandReceipt>('/commands', command, true)
  }
  async control(action: RoomAction, payload: object = {}) {
    const lifetime = this.lifetime
    const room = await this.mutate<RoomView>(action, payload)
    if (this.current(lifetime)) this.accept(room)
  }
  async chooseGame(gameId: string) {
    const lifetime = this.lifetime
    const room = await this.mutate<RoomView>('/game', { gameId })
    if (this.current(lifetime)) this.accept(room)
  }
  async remove(memberId: string) {
    const lifetime = this.lifetime
    const room = await this.mutate<RoomView>(`/members/${encodeURIComponent(memberId)}/remove`, {})
    if (this.current(lifetime)) this.accept(room)
  }
  async rename(name: string) {
    const lifetime = this.lifetime
    const room = await this.mutate<RoomView>('/name', { name })
    if (this.current(lifetime)) this.accept(room)
  }
}

export function errorMessage(cause: unknown) { return cause instanceof Error ? cause.message : 'The request did not finish. Please try again.' }
